import { Injectable, OnDestroy } from '@angular/core';
import {
  Observable,
  EMPTY,
  Subscription,
  defer,
  forkJoin,
  from,
  of,
  throwError,
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  scan,
  shareReplay,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { ApiHttpService } from '../../core/services/api-http.service';
import { ApiEndpointsService } from '../../core/services/api-endpoints.service';
import { AceAuthService } from '../../core/services/auth/ace-auth.service';
import { TeamMember } from './team-member.model';
import {
  AceEnvelope,
  AceTeamListData,
  AceTeamMemberDto,
} from './ace/ace-team-member.dto';
import { toTeamMember } from './ace/ace-team-member.mapper';
import { ActivityEntry, ActivityFeed } from './activity.model';
import { AceActivityResponseDto } from './ace/ace-activity.dto';
import { toActivityEntry, toActivityFeed } from './ace/ace-activity.mapper';

/**
 * Anticorruption layer over the ACE Team API. This is the only place that knows
 * ACE's response envelope, its endpoints, and the appRoles/roles quirk; callers
 * receive the app's own TeamMember model. The ACE access token is attached by
 * AceTokenInterceptor; AceAuthService supplies the cache context.
 */
@Injectable({
  providedIn: 'root',
})
export class TeamService implements OnDestroy {
  constructor(
    private apiHttpService: ApiHttpService,
    private apiEndpointsService: ApiEndpointsService,
    private aceAuthService: AceAuthService
  ) {
    this.contextSubscription = this.aceAuthService.contextChanges$.subscribe(
      () => {
        this.firmTeamCache.clear();
        this.lastActivityCache.clear();
      }
    );
  }

  // Cached firm-team stream, shared across the team page, the teammate profile,
  // and the management board so navigating between them doesn't re-fetch
  // /users/me + /team/members every time. Cached for the session (the team
  // directory changes rarely; a reload re-instantiates this root service). On
  // error the cache is dropped so a transient failure isn't replayed all session.
  private readonly firmTeamCache = new Map<string, Observable<TeamMember[]>>();
  private readonly contextSubscription: Subscription;

  // Cached "last activity per member" map, shared by remounts of the team grid
  // so navigation doesn't re-run the N+1 storm (there is no batch activity
  // endpoint — one call per member). Cached for the session like firmTeam$;
  // dropped on a top-level error so a transient failure isn't replayed all
  // session.
  private readonly lastActivityCache = new Map<
    string,
    Observable<Record<string, ActivityEntry | null>>
  >();

  // Loads the firm team with the current user prepended (since /team/members
  // excludes the caller).
  getFirmTeam(): Observable<TeamMember[]> {
    const scope = this.aceAuthService.getContextKey();
    const cached = this.firmTeamCache.get(scope);
    if (cached) return cached;

    const request$ = defer(() => {
      if (this.aceAuthService.getContextKey() !== scope) {
        return EMPTY;
      }
      return forkJoin({
        me: this.apiHttpService.get(this.apiEndpointsService.getCurrentUser()),
        team: this.apiHttpService.get(
          this.apiEndpointsService.getTeamMembers()
        ),
      });
    }).pipe(
      map(({ me, team }) => {
        const meData = (me as unknown as AceEnvelope<AceTeamMemberDto>)?.data;
        const teamData = (team as unknown as AceEnvelope<AceTeamListData>)
          ?.data;
        const self = meData
          ? toTeamMember(meData, { isCurrentUser: true })
          : null;
        const others = (teamData?.items ?? []).map(dto => toTeamMember(dto));
        return self ? [self, ...others] : others;
      }),
      catchError(err => {
        if (this.firmTeamCache.get(scope) === request$) {
          this.firmTeamCache.delete(scope);
        }
        return throwError(() => err);
      }),
      takeUntil(this.aceAuthService.contextChanges$),
      shareReplay({ bufferSize: 1, refCount: false }),
      filter(() => this.aceAuthService.getContextKey() === scope)
    );
    this.firmTeamCache.set(scope, request$);
    return request$;
  }

  // Cap on simultaneous /activity requests. There's no batch endpoint (one call
  // per member), so we bound concurrency rather than firing the whole team at
  // once — a 50-person team would otherwise queue 50 requests behind the
  // browser's per-host connection limit and stall the slowest behind the rest.
  private static readonly ACTIVITY_CONCURRENCY = 5;

  /**
   * Map of memberId -> that member's latest activity (or null when they have
   * none / it couldn't be loaded). One /activity?limit=1 call per member, run a
   * few at a time; the map is emitted incrementally (via scan) so the grid and
   * grid fills in as each member resolves instead of waiting for the slowest.
   * A single member's failure degrades to null and never sinks the whole map.
   * Cached for the session and shared across remounts of the team grid.
   */
  getLastActivityByMember(): Observable<Record<string, ActivityEntry | null>> {
    const scope = this.aceAuthService.getContextKey();
    const cached = this.lastActivityCache.get(scope);
    if (cached) return cached;

    const request$ = defer(() => {
      if (this.aceAuthService.getContextKey() !== scope) {
        return EMPTY;
      }
      return this.getFirmTeam();
    }).pipe(
      switchMap(members => {
        if (!members.length) {
          return of({} as Record<string, ActivityEntry | null>);
        }
        return from(members).pipe(
          mergeMap(
            m =>
              this.apiHttpService
                .get(
                  this.apiEndpointsService.getActivity({
                    userId: m.id,
                    tab: 'member',
                    limit: 1,
                  })
                )
                .pipe(
                  map(res => {
                    const items =
                      (res as unknown as AceEnvelope<AceActivityResponseDto>)
                        ?.data?.items ?? [];
                    return [
                      m.id,
                      items[0] ? toActivityEntry(items[0]) : null,
                    ] as const;
                  }),
                  catchError(() => of([m.id, null] as const))
                ),
            TeamService.ACTIVITY_CONCURRENCY
          ),
          scan((acc, [id, entry]) => {
            acc[id] = entry;
            return acc;
          }, {} as Record<string, ActivityEntry | null>)
        );
      }),
      catchError(err => {
        if (this.lastActivityCache.get(scope) === request$) {
          this.lastActivityCache.delete(scope);
        }
        return throwError(() => err);
      }),
      takeUntil(this.aceAuthService.contextChanges$),
      shareReplay({ bufferSize: 1, refCount: false }),
      filter(() => this.aceAuthService.getContextKey() === scope)
    );
    this.lastActivityCache.set(scope, request$);
    return request$;
  }

  ngOnDestroy(): void {
    this.contextSubscription.unsubscribe();
    this.firmTeamCache.clear();
    this.lastActivityCache.clear();
  }

  /**
   * The grouped, cursor-paginated activity feed for one member used on the
   * teammate profile.
   */
  getActivityFeed(userId: string, cursor?: string): Observable<ActivityFeed> {
    return this.runInCurrentContext(() =>
      this.apiHttpService
        .get(
          this.apiEndpointsService.getActivity({
            userId,
            tab: 'member',
            limit: 20,
            cursor,
          })
        )
        .pipe(
          map(res =>
            toActivityFeed(
              (res as unknown as AceEnvelope<AceActivityResponseDto>)?.data
            )
          )
        )
    );
  }

  getFundActivityFeed(
    fundId: string,
    cursor?: string
  ): Observable<ActivityFeed> {
    return this.runInCurrentContext(() =>
      this.apiHttpService
        .get(
          this.apiEndpointsService.getFundActivity({
            fundId,
            limit: 20,
            cursor,
          })
        )
        .pipe(
          map(res =>
            toActivityFeed(
              (res as unknown as AceEnvelope<AceActivityResponseDto>)?.data
            )
          )
        )
    );
  }

  private runInCurrentContext<T>(
    sourceFactory: () => Observable<T>
  ): Observable<T> {
    const scope = this.aceAuthService.getContextKey();
    return defer(() =>
      this.aceAuthService.getContextKey() === scope ? sourceFactory() : EMPTY
    ).pipe(
      takeUntil(this.aceAuthService.contextChanges$),
      filter(() => this.aceAuthService.getContextKey() === scope)
    );
  }
}
